import React, { useState, useEffect } from 'react';
import {
  Clock,
  TrendingUp,
  DollarSign,
  AlertCircle,
  Package,
  ShoppingCart,
  TrendingDown,
  RefreshCw,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useDashboardStats } from '../../hooks/useDashboard';
import { useExchangeRate } from '../../hooks/useExchangeRate';
import { formatCurrencyRounded } from '../../utils/formatters';

export const Dashboard: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showRevenue, setShowRevenue] = useState(false);
  const [showStockValue, setShowStockValue] = useState(false);
  const { data: stats, isLoading, isError } = useDashboardStats();
  const { data: exchangeRate, isLoading: rateLoading } = useExchangeRate();

  // Update time every minute (not every second)
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const formatDate = (date: Date): string => {
    const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
    const months = [
      'Ocak',
      'Şubat',
      'Mart',
      'Nisan',
      'Mayıs',
      'Haziran',
      'Temmuz',
      'Ağustos',
      'Eylül',
      'Ekim',
      'Kasım',
      'Aralık',
    ];
    const dayName = days[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${dayName}, ${day} ${month} ${hours}:${minutes}`;
  };

  // Use stats from backend
  const todaySalesCount = stats?.todaySalesCount ?? 0;
  const todaysGrossValue = stats?.todaysGrossValue ?? 0;
  const totalProducts = stats?.totalProductCount ?? 0;
  const lowStockProducts = stats?.lowStockCount ?? 0;
  const outOfStockProducts = stats?.outOfStockCount ?? 0;
  const totalStockValue = stats?.totalProductValue ?? 0;

  return (
    <div className="space-y-6">
      {/* Error Alert */}
      {isError && (
        <div className="card p-4 bg-warning/10 border-warning/20 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-warning flex-shrink-0" />
          <p className="text-sm text-foreground-secondary">
          Backend bağlantısı kurulamadı. Lütfen backend sunucusunun çalıştığından emin olun.
          </p>
        </div>
      )}

      {/* Top Row - Clock and Exchange Rate */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Clock Card */}
        <div className="lg:col-span-2 card p-6">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2.5 rounded-lg bg-accent/20">
                  <Clock className="w-5 h-5 text-accent" />
                </div>
                <h2 className="text-sm font-medium text-foreground-secondary uppercase tracking-wider">
                  Şu Anki Zaman
                </h2>
              </div>
              <div className="text-3xl font-semibold text-foreground">
                {formatDate(currentTime)}
              </div>
            </div>
          </div>
        </div>

        {/* Exchange Rate Card */}
        <div className="card p-6 bg-gradient-to-br from-warning/10 to-warning/5 border-warning/20">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground-secondary mb-1">
                Dolar/TL Kuru
              </p>
              <p className="text-xs text-foreground-tertiary">Güncel kur</p>
            </div>
            <div className="p-2.5 rounded-lg bg-warning/20">
              <DollarSign className="w-5 h-5 text-warning" />
            </div>
          </div>
          <div className="text-3xl font-bold text-foreground">
            {rateLoading ? (
              <div className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 animate-spin text-foreground-tertiary" />
                <span className="text-lg text-foreground-tertiary">Yükleniyor...</span>
              </div>
            ) : (
              <>
                <span className="text-2xl">$1 = </span>
                {exchangeRate?.toFixed(2) ?? '34.50'} ₺
              </>
            )}
          </div>
          <div className="mt-2 text-xs text-foreground-tertiary">
            Son güncelleme: {new Date().toLocaleTimeString('tr-TR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </div>
        </div>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          {/* Sales Count Card */}
        <div className="card-hover p-6 bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground-secondary mb-1">
                Günlük Satış
              </p>
              <p className="text-xs text-foreground-tertiary">Toplam adet</p>
            </div>
            <div className="p-2.5 rounded-lg bg-accent/20">
              <TrendingUp className="w-5 h-5 text-accent" />
            </div>
          </div>
          <div className="text-3xl font-bold text-foreground">
            {isLoading ? (
              <span className="inline-block w-16 h-8 bg-background-tertiary rounded animate-pulse" />
            ) : (
              todaySalesCount
            )}
          </div>
        </div>

          {/* Revenue Card */}
        <div 
          className="card-hover p-6 bg-gradient-to-br from-success/10 to-success/5 border-success/20 cursor-pointer relative group"
          onClick={() => setShowRevenue(!showRevenue)}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground-secondary mb-1">
                Günlük Gelir
              </p>
              <p className="text-xs text-foreground-tertiary">Toplam tutar</p>
            </div>
            <div className="flex items-center gap-2">
              <button 
                className="p-2 rounded-lg bg-success/20 hover:bg-success/30 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRevenue(!showRevenue);
                }}
              >
                {showRevenue ? (
                  <Eye className="w-4 h-4 text-success" />
                ) : (
                  <EyeOff className="w-4 h-4 text-success" />
                )}
              </button>
              <div className="p-2.5 rounded-lg bg-success/20">
                <DollarSign className="w-5 h-5 text-success" />
              </div>
            </div>
          </div>
          <div className="text-3xl font-bold text-foreground">
            {isLoading ? (
              <span className="inline-block w-32 h-8 bg-background-tertiary rounded animate-pulse" />
            ) : (
              <span className={showRevenue ? '' : 'filter blur-md select-none'}>
                {formatCurrencyRounded(todaysGrossValue)}
              </span>
            )}
          </div>
          {!showRevenue && !isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs text-foreground-tertiary bg-background-secondary px-3 py-1 rounded-full">
                Görmek için tıklayın
              </span>
            </div>
          )}
        </div>

        {/* Products Card */}
        <div className="card-hover p-6 bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground-secondary mb-1">
                Toplam Ürün
              </p>
              <p className="text-xs text-foreground-tertiary">Kayıtlı ürün</p>
            </div>
            <div className="p-2.5 rounded-lg bg-blue-500/20">
              <Package className="w-5 h-5 text-blue-500" />
            </div>
          </div>
          <div className="text-3xl font-bold text-foreground">{totalProducts}</div>
        </div>
      </div>

      {/* Bottom Row - Stock Alerts and Inventory Value */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        {/* Low Stock Alert */}
        <div className="card p-6 bg-gradient-to-br from-warning/10 to-warning/5 border-warning/20">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground-secondary mb-1">
                Düşük Stok
              </p>
              <p className="text-xs text-foreground-tertiary">10'dan az</p>
            </div>
            <div className="p-2.5 rounded-lg bg-warning/20">
              <AlertCircle className="w-5 h-5 text-warning" />
            </div>
          </div>
          <div className="text-3xl font-bold text-foreground">{lowStockProducts}</div>
          {lowStockProducts > 0 && (
            <div className="mt-2 text-xs text-warning">
              Stok yenileme gerekli
            </div>
          )}
        </div>

        {/* Out of Stock Alert */}
        <div className="card p-6 bg-gradient-to-br from-error/10 to-error/5 border-error/20">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground-secondary mb-1">
                Tükendi
              </p>
              <p className="text-xs text-foreground-tertiary">Stokta yok</p>
            </div>
            <div className="p-2.5 rounded-lg bg-error/20">
              <TrendingDown className="w-5 h-5 text-error" />
            </div>
          </div>
          <div className="text-3xl font-bold text-foreground">{outOfStockProducts}</div>
          {outOfStockProducts > 0 && (
            <div className="mt-2 text-xs text-error">
              Acil stok girişi gerekli
            </div>
          )}
        </div>

        {/* Inventory Value */}
        <div 
          className="card p-6 bg-gradient-to-br from-success/10 to-success/5 border-success/20 cursor-pointer relative group"
          onClick={() => setShowStockValue(!showStockValue)}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground-secondary mb-1">
                Stok Değeri
              </p>
              <p className="text-xs text-foreground-tertiary">Toplam envanter</p>
            </div>
            <div className="flex items-center gap-2">
              <button 
                className="p-2 rounded-lg bg-success/20 hover:bg-success/30 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowStockValue(!showStockValue);
                }}
              >
                {showStockValue ? (
                  <Eye className="w-4 h-4 text-success" />
                ) : (
                  <EyeOff className="w-4 h-4 text-success" />
                )}
              </button>
              <div className="p-2.5 rounded-lg bg-success/20">
                <ShoppingCart className="w-5 h-5 text-success" />
              </div>
            </div>
          </div>
          <div className="text-2xl font-bold text-foreground">
            <span className={showStockValue ? '' : 'filter blur-md select-none'}>
              {formatCurrencyRounded(totalStockValue)}
            </span>
          </div>
          <div className="mt-2 text-xs text-foreground-tertiary">
            {totalProducts} ürün toplamı
          </div>
          {!showStockValue && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs text-foreground-tertiary bg-background-secondary px-3 py-1 rounded-full">
                Görmek için tıklayın
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
